import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { queryClient } from "..";
import { scheduleApi } from "../api/schedule-api";
import {
  setSchedulePageAction,
  setSchedulePageAllAction,
  setSchedulePageByDtoAction,
} from "../app/reducers/schedule-slice";
import { store } from "../app/store";
import { SpecificDatePayload } from "../types/common/common-payload";
import { Confirm } from "../types/confirm";
import {
  AssignSchedulePayload,
  CreateSchedulePayload,
  SearchSchedulePageByPeriodPayload,
} from "../types/schedule/schedule-payload";
import { endGlobalLoadingAction } from "../app/reducers/loading-slice";

export enum ScheduleQueryKey {
  Create = "CreateSchedule",
  GetById = "GetScheduleById",
  Page = "SchedulePage",
  PageAll = "SchedulePageAll",
  Assign = "AssignSchedule",
  DeleteAll = "DeleteScheduleAll",
}

export const useCreateScheduleMutation = () => {
  return useMutation(
    ScheduleQueryKey.Create,
    (payload: CreateSchedulePayload) => scheduleApi.create(payload),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(ScheduleQueryKey.GetById);
        toast.success(Confirm.Confirm);
      },
    }
  );
};

export const useGetScheduleByIdQuery = (payload: string) => {
  return useQuery(
    [ScheduleQueryKey.GetById, payload],
    () => scheduleApi.getById(payload),
    {
      enabled: !!payload,
      onSuccess: ({ data }) => {
        const scheduleDto = data;
        store.dispatch(
          setSchedulePageByDtoAction({
            scheduleDto,
          })
        );
      },
    }
  );
};

export const useGetSchedulePageByPeriodQuery = (
  payload: SearchSchedulePageByPeriodPayload
) => {
  return useQuery(
    [ScheduleQueryKey.Page, payload],
    () => scheduleApi.search(payload),
    {
      enabled: payload.hasOwnProperty("page"),
      onSuccess: ({ data }) => {
        const schedulePageDto = data;
        store.dispatch(
          setSchedulePageAction({
            schedulePageDto,
          })
        );
        store.dispatch(endGlobalLoadingAction());
      },
        onError: () => {
            store.dispatch(endGlobalLoadingAction());
        }
    }
  );
};

export const useGetSchedulePageAllBySpecificDateQuery = (
  payload: SpecificDatePayload
) => {
  return useQuery(
    [ScheduleQueryKey.PageAll, payload],
    () => scheduleApi.searchAll(payload),
    {
      enabled: payload.hasOwnProperty("specificDate"),
      onSuccess: ({ data }) => {
        const schedulePageAllDto = data;
        store.dispatch(
          setSchedulePageAllAction({
            schedulePageAllDto,
          })
        );
      },
    }
  );
};

export const useAssignScheduleMutation = () => {
  return useMutation(
    ScheduleQueryKey.Assign,
    (params: { scheduleId: string; payload: AssignSchedulePayload }) =>
      scheduleApi.assign(params.scheduleId, params.payload),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(ScheduleQueryKey.GetById);
        toast.success(Confirm.Confirm);
      },
    }
  );
};

export const useDeleteSchedulesAllMutation = () => {
  return useMutation(
    ScheduleQueryKey.DeleteAll,
    (specificDate: string) => scheduleApi.deleteAll(specificDate),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ScheduleQueryKey.Page);
        toast.success(Confirm.Delete);
      },
    }
  );
};
